import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "@/components/brand/Logo";
import { FEATURE_NAV } from "@/data/features";

const MAIN_NAV = [["Features", "/features"], ["Time Clock", "/time-clock"], ["GPS & Geofencing", "/gps-geofencing"], ["Rosters", "/rosters"], ["Pricing", "/pricing"], ["About", "/about"], ["Contact", "/contact"]];
const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");
const linkCls = ({ isActive }) => `text-sm font-medium transition-colors hover:text-[#00A85C] ${isActive ? "text-[#00A85C]" : "text-slate-600"}`;

const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-slate-100 bg-white/90 backdrop-blur" data-testid="site-header">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-3 py-3 sm:gap-4 sm:px-6 lg:px-8">
        <div className="min-w-0 shrink"><Logo /></div>
        <nav className="hidden items-center gap-6 xl:flex">
          {MAIN_NAV.map(([n, to]) => <NavLink key={to} to={to} className={linkCls} data-testid={`site-nav-${slug(n)}`}>{n}</NavLink>)}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" className="hidden sm:block"><Button variant="ghost" data-testid="site-login-btn">Log in</Button></Link>
          <Link to="/register"><Button className="rounded-full bg-[#00C16B] px-3 text-sm hover:bg-[#00A85C] sm:px-4" data-testid="site-register-btn"><span className="sm:hidden">Free trial</span><span className="hidden sm:inline">Start free trial</span></Button></Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild><button className="xl:hidden" data-testid="site-mobile-menu-btn" aria-label="Open menu"><Menu className="h-6 w-6 text-slate-700" /></button></SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <nav className="mt-8 flex flex-col gap-4">
                {[...MAIN_NAV, ["Timesheets", "/timesheets"], ["Announcements", "/announcements"], ["Payroll & Reporting", "/payroll-reporting"], ["Log in", "/login"]].map(([n, to]) => (
                  <NavLink key={to} to={to} onClick={() => setOpen(false)} className={linkCls} data-testid={`mobile-nav-${slug(n)}`}>{n}</NavLink>
                ))}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

const Footer = () => (
  <footer className="border-t border-slate-200 bg-white py-12" data-testid="site-footer">
    <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 md:grid-cols-4 lg:px-8">
      <div><Logo size="sm" /><p className="mt-3 text-sm text-slate-500">Your Team. Your Time. All in One Place.</p></div>
      {[["Product", [["Features", "/features"], ...FEATURE_NAV, ["Pricing", "/pricing"]]], ["Company", [["About Us", "/about"], ["Contact", "/contact"], ["Login", "/login"], ["Register Business", "/register"]]], ["Legal", [["Privacy Policy", "/privacy"], ["Terms & Conditions", "/terms"]]]].map(([h, items]) => (
        <div key={h}>
          <p className="text-sm font-semibold text-[#0B2D5B]">{h}</p>
          <ul className="mt-3 space-y-2">{items.map(([n, to]) => <li key={to}><Link to={to} className="text-sm text-slate-500 hover:text-[#00A85C]" data-testid={`footer-link-${slug(n)}`}>{n}</Link></li>)}</ul>
        </div>
      ))}
    </div>
    <p className="mx-auto mt-10 max-w-7xl px-4 text-xs text-slate-400 sm:px-6 lg:px-8">© {new Date().getFullYear()} Clockaroo · clockaroo.com.au · [ABN placeholder]</p>
  </footer>
);

export default function SiteLayout() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main><Outlet /></main>
      <Footer />
    </div>
  );
}

export const PageHero = ({ eyebrow, title, intro, children }) => (
  <section className="border-b border-slate-100 bg-slate-50">
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
      {eyebrow && <p className="rise text-xs font-semibold uppercase tracking-[0.2em] text-[#E07800]">{eyebrow}</p>}
      <h1 className="rise rise-1 mt-3 max-w-3xl text-4xl font-extrabold tracking-tight text-[#0B2D5B] sm:text-5xl">{title}</h1>
      {intro && <p className="rise rise-2 mt-5 max-w-2xl text-base text-slate-600 sm:text-lg">{intro}</p>}
      {children}
    </div>
  </section>
);
